<script setup lang="ts">
import { computed, defineProps } from 'vue';
import { storeToRefs } from 'pinia';
import PaymentCardTop from '@/dashboard/vue/components/popups/PaymentPopup/PaymentCardTop.vue';
import PaymentCardButton from '@/dashboard/vue/components/popups/PaymentPopup/PaymentCardButton.vue';
import { SubscriptionPeriod, SubscriptionType } from '@/dashboard/types/subscription';
import { SubscriptionBase } from '@/dashboard/types/subscriptionBase';
import DropdownArrowIcon from '@/dashboard/vue/components/icons/DropdownArrowIcon.vue';
import Dropdown from '@/dashboard/vue/components/common/Dropdown/Dropdown.vue';
import ContextMenuButton from '@/dashboard/vue/components/common/ContextMenu/ContextMenuButton.vue';
import { usePurchaseStore } from '@/dashboard/store/purchaseStore';
import PaymentBaseDescription from '@/dashboard/vue/components/popups/PaymentPopup/PaymentBaseDescription.vue';
import PaymentProDescription from '@/dashboard/vue/components/popups/PaymentPopup/PaymentProDescription.vue';

const props = defineProps<{
  type: SubscriptionType;
}>();

const purchaseStore = usePurchaseStore();
const { period, subscriptions, selectedPlan, currentSubscription, prices } = storeToRefs(purchaseStore);

const filteredSubscriptions = computed(() => subscriptions.value.filter((s) => s.period === period.value));
const isCurrent = computed(() => selectedPlan?.value?.id === currentSubscription?.value?.price_id);

function dropdownTitleVolume(): string {
  const volume = (selectedPlan?.value?.description ?? '').split(' ').pop() ?? '';
  return `${volume}`;
}

function dropdownItemTitle(subscription: SubscriptionBase): string {
  return subscription.description.split(' ').pop() ?? '';
}

function dropdownItemSimilarPrice(subscription: SubscriptionBase): string[] {
  const value = dropdownItemTitle(subscription);
  return subscriptions.value
    .filter((item) => item.description.split(' ').pop() === value)
    .map((item) => ({
      id: item.id,
      period: item.period,
      price: prices.value.find((price) => price.id === item.id),
    }))
    .map((pri) => {
      const isYear = pri.period === 'year';
      const label = isYear ? 'annually' : 'monthly';
      const price = isYear ? (+pri.price!.amount / 12).toFixed(2) : (+pri.price!.amount).toFixed(2);
      return `$${price} user/mo billed ${label}`;
    });
}
</script>

<template>
  <div class="flex h-[949px] w-[351px] flex-col rounded-24 border-[1px] border-solid border-gray-300 text-black dark:border-gray-850 dark:text-white">
    <PaymentCardTop :period="period" :type="props.type" :prices="prices" :selected-plan="selectedPlan" />

    <div class="mx-m flex flex-1 flex-col pb-[25px]">
      <div class="mb-[33px] flex">
        <PaymentCardButton :type="props.type" />
      </div>

      <div class="flex flex-col text-left">
        <span class="text-caption-s dark:text-gray-500">Storage</span>

        <span v-if="props.type === 'basic'" class="text-caption-s mb-[36px] mt-xxs">
          <span class="flex items-end">
            <span class="text-[24px] font-bold">100MB</span>
          </span>
        </span>

        <Dropdown v-else class="mx-[-12px] mb-[14px]" :body-class="'!w-full ml-zero max-h-[500px] overflow-scroll gap-[2px] p-[6px]'">
          <template #title>
            <div
              class="flex h-xxl items-center justify-between rounded-8 border-[1px] border-solid border-gray-300 pl-[12px] pr-s dark:border-gray-700"
            >
              <span class="text-caption-s">
                <span class="flex items-center gap-xs">
                  <span class="text-[24px] font-bold">{{ dropdownTitleVolume() }}</span>
                  <span v-if="isCurrent" class="text-content-m">Current</span>
                </span>
              </span>
              <DropdownArrowIcon :large="true" class="stroke-black dark:stroke-gray-500" width="16" height="9" />
            </div>
          </template>
          <template #body>
            <div
              v-for="sub in filteredSubscriptions"
              :key="sub.id"
              class="group/select flex w-full flex-col"
              :class="{
                'even:[&_+div_button_div_div>span]:hover:bg-[#DDFFD1] dark:even:[&_+div_button_div_div>span]:hover:bg-[#305423]': true,
              }"
            >
              <ContextMenuButton class="!h-[119px]" :need-dark="true" @click="() => purchaseStore.selectPlan(sub)">
                <div class="flex flex-col items-start gap-y-[9px] whitespace-nowrap">
                  <span class="text-caption-s text-[24px] font-bold text-black dark:text-white">
                    <span class="px-[18px]">{{ dropdownItemTitle(sub) }}</span>
                  </span>
                  <div class="flex flex-col items-start">
                    <span
                      v-for="item in dropdownItemSimilarPrice(sub)"
                      :key="item"
                      class="text-caption-s relative rounded-4 text-gray-600 first-of-type:group-hover/select:bg-[#DDFFD1] dark:text-gray-450 dark:first-of-type:group-hover/select:bg-[#305423]"
                    >
                      <span class="px-[18px]">{{ item }}</span>
                    </span>
                  </div>
                </div>
              </ContextMenuButton>
            </div>
          </template>
        </Dropdown>
      </div>

      <PaymentBaseDescription v-if="props.type === 'basic'" />
      <PaymentProDescription v-else />
    </div>
  </div>
</template>
